import React, { useEffect } from 'react';
import { Shield, Lock, Eye, FileText, HelpCircle } from 'lucide-react';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';

export default function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy | Eclipse Group & Voyara AI",
    "description": "The Privacy Policy for Voyara AI and Eclipse Group, detailing data collection, processing, and protection rules."
  };

  const sections = [
    { id: 'intro', title: 'Introduction', icon: FileText },
    { id: 'collection', title: 'Information We Collect', icon: Eye },
    { id: 'usage', title: 'How We Use Data', icon: Shield },
    { id: 'security', title: 'Data Security', icon: Lock },
    { id: 'rights', title: 'Your Rights & Options', icon: HelpCircle },
  ];

  const handleScrollTo = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="bg-black min-h-screen text-white font-sans selection:bg-blue-500/30">
      <SEO 
        title="Privacy Policy — Eclipse Group"
        description="Privacy policy and data protection standards for Voyara AI by Eclipse Private Limited and Eclipse Media."
        canonical="https://www.eclipsegroup.in/privacy"
        schema={schema}
      />

      <main className="pt-32 pb-24 global-container max-w-4xl mx-auto px-6 relative z-10">
        <Breadcrumbs trail={[{ name: 'Privacy Policy', path: '/privacy' }]} />

        {/* Hero Section */}
        <div className="mb-14">
          <span className="text-[11px] text-blue-400 font-bold tracking-[0.25em] uppercase bg-blue-500/10 px-3 py-1.5 rounded border border-blue-500/20">
            Legal Infrastructure
          </span>
          <h1 className="font-serif text-[clamp(2.5rem,5vw,4.5rem)] leading-[1.05] tracking-tight mt-6 mb-4">
            Privacy Policy
          </h1>
          <p className="text-white/40 font-mono text-[11px] uppercase tracking-wider">
            Last Updated: May 22, 2026 &nbsp;|&nbsp; Document Ref: PL-VOY-001
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Sticky Sidebar Navigation (Desktop) */}
          <aside className="lg:col-span-4 sticky top-28 hidden lg:block rounded-2xl border border-white/5 bg-white/[0.01] p-6 liquid-glass">
            <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-white/30 mb-6">
              Table of Contents
            </h2>
            <nav className="space-y-4">
              {sections.map((sec, idx) => (
                <a
                  key={sec.id}
                  href={`#${sec.id}`}
                  onClick={(e) => handleScrollTo(e, sec.id)}
                  className="flex items-center gap-3 text-[13px] text-white/50 hover:text-white transition-colors group cursor-pointer"
                >
                  <span className="text-[10px] font-mono text-white/20 group-hover:text-blue-400 transition-colors">
                    0{idx + 1}
                  </span>
                  <span className="font-medium">{sec.title}</span>
                </a>
              ))}
            </nav>
          </aside>

          {/* Policy Text Column */}
          <div className="lg:col-span-8 space-y-16">
            
            {/* Direct Answer/Executive Summary */}
            <div className="p-6 rounded-2xl bg-white/[0.01] border border-white/5 relative overflow-hidden liquid-glass">
              <h2 className="text-xs font-bold tracking-[0.1em] uppercase text-white/40 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-blue-400" />
                Commitment Statement
              </h2>
              <p className="text-white/80 text-base leading-relaxed font-light">
                Eclipse Group (Eclipse Private Limited and Eclipse Media) is dedicated to protecting your privacy. We collect minimal personal information to facilitate early access requests and to refine our travel orchestration technology, Voyara AI. We never sell, lease, or monetize your personal data.
              </p>
            </div>

            {/* SEC 01 */}
            <section id="intro" className="scroll-mt-28">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono text-xs text-blue-400 bg-blue-500/10 px-2 py-1 rounded">SEC. 01</span>
                <h2 className="font-serif text-2xl tracking-tight text-white">Introduction</h2>
              </div>
              <div className="space-y-4 text-white/70 font-light leading-relaxed text-[15px]">
                <p>
                  Welcome to Voyara AI, an intelligent travel planning and orchestration system developed and operated by <strong>Eclipse Private Limited</strong> (also referred to as "Eclipse Group", "we", "us", or "our") and supported by <strong>Eclipse Media</strong>.
                </p>
                <p>
                  This Privacy Policy details our practices concerning the collection, use, and disclosure of information provided by users when interacting with our website located at <a href="https://www.eclipsegroup.in" className="text-blue-400 hover:underline">www.eclipsegroup.in</a>, our landing systems, waitlists, and related features.
                </p>
                <p>
                  By submitting your information through our early access form or interacting with our services, you consent to the details described in this document. If you do not agree with these practices, please refrain from providing personal details.
                </p>
              </div>
            </section>

            {/* SEC 02 */}
            <section id="collection" className="scroll-mt-28">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono text-xs text-blue-400 bg-blue-500/10 px-2 py-1 rounded">SEC. 02</span>
                <h2 className="font-serif text-2xl tracking-tight text-white">Information We Collect</h2>
              </div>
              <div className="space-y-4 text-white/70 font-light leading-relaxed text-[15px]">
                <p>
                  We aim to collect only what is strictly necessary to provide an elite user experience and maintain network security. The categories of information we process include:
                </p>
                <ul className="space-y-3 pl-4 list-disc marker:text-blue-400/50">
                  <li>
                    <strong>Early Access Details:</strong> When you submit a request for the Voyara AI waitlist, we collect your email address, and optionally, your name, organizational alignment, and system preferences.
                  </li>
                  <li>
                    <strong>Device and Connection Metrics:</strong> We automatically capture basic environment metrics including browser type, operating system, language preferences, referrer URLs, and network IP addresses for load balancing and security analytics.
                  </li>
                  <li>
                    <strong>Interactive Telemetry:</strong> To refine our product's performance and design responsive layouts, we track user movement across elements (clicks, scroll depths, and navigation pathways) using anonymous session variables.
                  </li>
                </ul>
              </div>
            </section>

            {/* SEC 03 */}
            <section id="usage" className="scroll-mt-28">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono text-xs text-blue-400 bg-blue-500/10 px-2 py-1 rounded">SEC. 03</span>
                <h2 className="font-serif text-2xl tracking-tight text-white">How We Use Information</h2>
              </div>
              <div className="space-y-4 text-white/70 font-light leading-relaxed text-[15px]">
                <p>
                  Your information is utilized strictly to provide, monitor, and optimize our travel systems. Specifically, we use collected data to:
                </p>
                <ul className="space-y-3 pl-4 list-disc marker:text-blue-400/50">
                  <li>Manage your early access registration and waitlist position.</li>
                  <li>Transmit authentic updates regarding Voyara AI releases, invitations, and features.</li>
                  <li>Audit page loading times, responsive view stability, and user interfaces (e.g. tracking layout stability on narrow viewports).</li>
                  <li>Detect, investigate, and prevent automated abuse, spam registrations, or malicious attacks on our API layers.</li>
                </ul>
              </div>
            </section>

            {/* SEC 04 */}
            <section id="security" className="scroll-mt-28">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono text-xs text-blue-400 bg-blue-500/10 px-2 py-1 rounded">SEC. 04</span>
                <h2 className="font-serif text-2xl tracking-tight text-white">Data Security & Storage</h2>
              </div>
              <div className="space-y-4 text-white/70 font-light leading-relaxed text-[15px]">
                <p>
                  Security is integrated into the core architecture of the Eclipse Group ecosystem. We implement industry-leading technical measures, including:
                </p>
                <p>
                  All telemetry and user details are stored securely using cloud infrastructure hosted under rigorous encryption standards (including TLS/SSL during transfer and AES-256 at rest). Access is restricted to authorized personnel who require details to build Voyara AI systems.
                </p>
                <p>
                  While we utilize robust firewalls and regular audits, no network system is completely impervious. We recommend protecting your details online and avoiding sending highly sensitive data through non-encrypted channels.
                </p>
              </div>
            </section>

            {/* SEC 05 */}
            <section id="rights" className="scroll-mt-28">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono text-xs text-blue-400 bg-blue-500/10 px-2 py-1 rounded">SEC. 05</span>
                <h2 className="font-serif text-2xl tracking-tight text-white">Your Rights & Options</h2>
              </div>
              <div className="space-y-4 text-white/70 font-light leading-relaxed text-[15px]">
                <p>
                  We fully respect your sovereignty over your personal data. Regardless of your physical location, you hold the following rights:
                </p>
                <ul className="space-y-3 pl-4 list-disc marker:text-blue-400/50">
                  <li>
                    <strong>Access and Portability:</strong> You may request a complete file containing any personal details we store associated with your email address.
                  </li>
                  <li>
                    <strong>Rectification or Update:</strong> You may request corrections to any obsolete or inaccurate information we maintain.
                  </li>
                  <li>
                    <strong>Erasure (Right to be Forgotten):</strong> You may request the absolute deletion of your waitlist details at any time. Upon verified request, we will purge your email from our distribution nodes within 72 hours.
                  </li>
                </ul>
                <p className="pt-4">
                  To exercise any of these options, or if you have questions regarding this document, please contact our privacy compliance team directly at: <a href="mailto:contact@eclipsegroup.in" className="text-blue-400 hover:underline">contact@eclipsegroup.in</a>.
                </p>
              </div>
            </section>

          </div>
        </div>
      </main>
    </div>
  );
}
